import React from 'react';
import { Navbar } from '../components/Navbar';
import { ClompApi } from '../api/ClompApi';
import './NewsPage.css';

const api = new ClompApi();

const TITLE_MAX = 120;
const BODY_MAX = 5000;

const dateFormat = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
};

// Turn any http(s) URLs in a post body into links
function linkify(text) {
    return text.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
        i % 2 === 1 ?
            <a key={i} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
            :
            part
    );
}

export class NewsPage extends React.Component {

    state = {
        posts: [],
        loading: true,
        error: false,
        // Staff and above can publish, edit and delete posts from this page
        canPost: false,
        // What the post form is open for: null (closed), 'new', or the id of the post being edited
        editing: null,
        title: '',
        body: '',
        saving: false,
        postError: null,
        // The id of the post whose Delete button has been pressed and is waiting to be confirmed
        confirmingDelete: null,
        deleting: false,
        deleteError: null
    };

    componentDidMount() {
        api.news.getNews().then((posts) => {
            this.setState({ posts: posts, loading: false });
        }).catch((err) => {
            console.log('Error loading news: ', err);
            this.setState({ loading: false, error: true });
        });

        if (localStorage.getItem('token') !== null) {
            api.admin.getAccess().then((access) => {
                this.setState({ canPost: Boolean(access.staff) });
            }).catch(() => {});
        }
    }

    startNew = () => {
        this.setState({ editing: 'new', title: '', body: '', postError: null, confirmingDelete: null });
    }

    startEdit(post) {
        this.setState({ editing: post.id, title: post.title, body: post.body, postError: null, confirmingDelete: null });
    }

    save = (e) => {
        e.preventDefault();
        let { editing, title, body } = this.state;
        this.setState({ saving: true, postError: null });
        let request = editing === 'new' ? api.news.addPost(title, body) : api.news.editPost(editing, title, body);
        request.then((result) => {
            let posts = editing === 'new' ?
                [result.post, ...this.state.posts]
                :
                this.state.posts.map((post) => (post.id === editing ? result.post : post));
            this.setState({ posts, error: false, editing: null, saving: false, title: '', body: '' });
        }).catch((err) => {
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({ saving: false, postError: message || "Couldn't save the post. Please try again." });
        });
    }

    deletePost(post) {
        this.setState({ deleting: true, deleteError: null });
        api.news.deletePost(post.id).then(() => {
            this.setState({ posts: this.state.posts.filter((other) => other.id !== post.id), confirmingDelete: null, deleting: false });
        }).catch((err) => {
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({ deleting: false, deleteError: message || "Couldn't delete the post. Please try again." });
        });
    }

    // The form for a new post, or for changing an existing one
    renderEditor() {
        let { editing, title, body, saving, postError } = this.state;
        let isNew = editing === 'new';
        return (
            <form className="news-editor" onSubmit={this.save}>
                <h2>{isNew ? 'New post' : 'Edit post'}</h2>
                <label htmlFor="news-title">Title</label>
                <input id="news-title" type="text" maxLength={TITLE_MAX} required autoFocus value={title}
                    onChange={(e) => this.setState({ title: e.target.value })} />
                <label htmlFor="news-body">Post</label>
                <textarea id="news-body" rows="8" maxLength={BODY_MAX} required value={body}
                    onChange={(e) => this.setState({ body: e.target.value })} />
                <p className="news-editor-hint">Leave a blank line between paragraphs. Web addresses starting with http turn into links.</p>
                <div className="news-editor-actions">
                    <span className="news-editor-count">{body.length} / {BODY_MAX}</span>
                    <button type="button" className="news-btn news-btn-ghost" disabled={saving} onClick={() => this.setState({ editing: null, postError: null })}>Cancel</button>
                    <button type="submit" className="news-btn news-btn-primary" disabled={saving || !title.trim() || !body.trim()}>
                        {isNew ? (saving ? 'Publishing...' : 'Publish') : (saving ? 'Saving...' : 'Save changes')}
                    </button>
                </div>
                {postError ? <p className="news-editor-error" role="alert">{postError}</p> : null}
            </form>
        );
    }

    // Staff controls under a post: Edit and Delete, with Delete asking once before it goes through
    renderPostActions(post) {
        let { confirmingDelete, deleting, deleteError } = this.state;
        if (confirmingDelete === post.id) {
            return (
                <div className="news-post-actions news-post-confirm" role="group" aria-label={`Delete ${post.title}`}>
                    <span>Delete this post for good?</span>
                    <button type="button" className="news-btn news-btn-small news-btn-danger" disabled={deleting} onClick={() => this.deletePost(post)}>{deleting ? 'Deleting...' : 'Yes, delete'}</button>
                    <button type="button" className="news-btn news-btn-small news-btn-ghost" disabled={deleting} autoFocus onClick={() => this.setState({ confirmingDelete: null, deleteError: null })}>Keep it</button>
                    {deleteError ? <span className="news-editor-error" role="alert">{deleteError}</span> : null}
                </div>
            );
        }
        return (
            <div className="news-post-actions">
                <button type="button" className="news-btn news-btn-small news-btn-ghost" onClick={() => this.startEdit(post)}>Edit</button>
                <button type="button" className="news-btn news-btn-small news-btn-ghost" onClick={() => this.setState({ confirmingDelete: post.id, deleteError: null })}>Delete</button>
            </div>
        );
    }

    renderPosts() {
        if (this.state.loading) {
            return <p className="news-status">Loading news...</p>;
        }
        if (this.state.error) {
            return <p className="news-status">Couldn't load the news right now. Please try again later.</p>;
        }
        if (this.state.posts.length === 0) {
            return <p className="news-status">No news yet. Check back soon!</p>;
        }

        return (
            <ul className="news-list">
                {this.state.posts.map((post) => (
                    this.state.canPost && this.state.editing === post.id ?
                        // the post being edited turns into the form, in place
                        <li key={post.id}>{this.renderEditor()}</li>
                        :
                        <li className="news-post" key={post.id}>
                            <h2>{post.title}</h2>
                            <time className="news-post-date" dateTime={post.postedAt}>
                                {new Date(post.postedAt).toLocaleString(undefined, dateFormat)}
                                {post.editedAt ? <span className="news-post-edited" title={`Edited ${new Date(post.editedAt).toLocaleString(undefined, dateFormat)}`}> · Edited</span> : null}
                            </time>
                            <p className="news-post-body">{linkify(post.body)}</p>
                            {this.state.canPost ? this.renderPostActions(post) : null}
                        </li>
                ))}
            </ul>
        );
    }

    render() {

        return (
            <>
                <Navbar history={this.props.history}></Navbar>
                <div className="news-page">
                    <div className="news-page-header">
                        <h1>News</h1>
                        {this.state.canPost && this.state.editing === null ?
                            <button type="button" className="news-btn news-btn-primary" onClick={this.startNew}>New post</button>
                            : null}
                    </div>
                    {this.state.canPost && this.state.editing === 'new' ? this.renderEditor() : null}
                    {this.renderPosts()}
                </div>
            </>
        );

    }

}
